import { cloneDeep } from "lodash";
import { genSelectOpts } from "rhombus-react";

import createReducer from "./createReducer";
import { buildAddVersionsToLibraryMessage, buildAssetGenerationFailMessage } from "utils/common";
import { SUPPRESS_ALL_GROWL, SUPPRESS_SUCCESS_GROWL } from "utils/constants";
import { loadConcept } from "./ConceptDetails";
import { fetchCreativesById } from "./Creatives";
import { GENERATE_VERSIONS } from "widgets/grid/cells/CreativeActionLists/VersionActions";
import { generateVariations, getVariableBounds } from "widgets/modals/creatives/GenerateVersionsAI/utils";
import { CREATIVES_ACTIONS } from "./actions/Creatives";

const VERSION_GENERATION_ACTIONS = {
	RESET: "VERSION_GENERATION_RESET",

	LOAD_SOURCE_VERSIONS_AJAX: "VERSION_GENERATION_LOAD_SOURCE_VERSIONS_AJAX",
	LOAD_SOURCE_VERSIONS_SUCCESS: "VERSION_GENERATION_LOAD_SOURCE_VERSIONS_SUCCESS",
	LOAD_SOURCE_VERSIONS_ERROR: "VERSION_GENERATION_LOAD_SOURCE_VERSIONS_ERROR",

	LOAD_CREATIVE_VARIABLES: "VERSION_GENERATION_LOAD_CREATIVE_VARIABLES",
	LOAD_CREATIVES: "VERSION_GENERATION_LOAD_CREATIVES",

	GENERATE_ASSETS_AJAX: "GENERATE_ASSETS_AJAX",
	GENERATE_ASSETS_SUCCESS: "GENERATE_ASSETS_SUCCESS",
	GENERATE_ASSETS_ERROR: "GENERATE_ASSETS_ERROR",

	CREATE_VERSIONS_AND_ASSETS_AJAX: "CREATE_VERSIONS_AND_ASSETS_AJAX",
	CREATE_VERSIONS_AND_ASSETS_SUCCESS: "CREATE_VERSIONS_AND_ASSETS_SUCCESS",
	CREATE_VERSIONS_AND_ASSETS_ERROR: "CREATE_VERSIONS_AND_ASSETS_ERROR",

	RESET_VARIATION_PAGE: "RESET_VARIATION_PAGE",
	ADD_VARIATION_PAGE: "ADD_VARIATION_PAGE",
	DISCARD_ASSET: "VERSION_GENERATION_DISCARD_ASSET",

	GET_UNIQUE_VERSION_NAMES_AJAX: "VERSION_GENERATION_GET_UNIQUE_VERSION_NAMES_AJAX",
	GET_UNIQUE_VERSION_NAMES_SUCCESS: "VERSION_GENERATION_GET_UNIQUE_VERSION_NAMES_SUCCESS",
	GET_UNIQUE_VERSION_NAMES_ERROR: "VERSION_GENERATION_GET_UNIQUE_VERSION_NAMES_ERROR",

	GET_METADATA_ENTRIES_BY_TYPE_AJAX: "GET_METADATA_ENTRIES_BY_TYPE_AJAX",
	GET_METADATA_ENTRIES_BY_TYPE_SUCCESS: "GET_METADATA_ENTRIES_BY_TYPE_SUCCESS",
	GET_METADATA_ENTRIES_BY_TYPE_ERROR: "GET_METADATA_ENTRIES_BY_TYPE_ERROR",
};

const VARIATION_RPP = 10;

const initialState = {
	isVariablesLoaded: false,
	variables: [],

	sourceVersionName: "default",
	sourceVersions: [],

	isCreativesLoaded: false,
	creatives: [],
	uniqueVersionNames: {
		items: [],
		totalItems: 0,
		totalPages: 1,
		rpp: 50,
		page: 1,
		isLoading: false,
	},

	metadata: {},

	isGeneratingAssets: false,
	generatedAssets: {},

	variations: [],

	numVariationsShown: VARIATION_RPP,
};

export function reset(){
	return (dispatch) => {
		return dispatch({
			type: VERSION_GENERATION_ACTIONS.RESET,
		});
	};
}

export function loadVersionsByName(libraryId, versionName="default"){
	return async (dispatch, getState) => {
		try {
			const validCreatives = getState().versionGeneration?.creatives || []; /*
				Invalid creatives are filtered out before being added to the state,
				so we use the state as the source of truth for which variables we need to fetch.
			*/
			const numCreatives = validCreatives.length;
			if(!numCreatives){ return; }

			const creativeIds = validCreatives.map(creative => creative.id);
			return dispatch({
				type: VERSION_GENERATION_ACTIONS.LOAD_SOURCE_VERSIONS_AJAX,
				AJAX: {
					url: `ui/creative-libraries/${libraryId}/versions`,
					params: {
						advancedFilter: `all(in(creativeId,${creativeIds.join(',')}),eq(name,${versionName}))`,
						includeCropMetadata:true,
					},
					success: VERSION_GENERATION_ACTIONS.LOAD_SOURCE_VERSIONS_SUCCESS,
					error: VERSION_GENERATION_ACTIONS.LOAD_SOURCE_VERSIONS_ERROR,
				},
				meta: SUPPRESS_SUCCESS_GROWL,
				versionName,
			});
		} catch(error){
			return error;
		}
	};
}

export function loadConceptWithVariables(libraryId, conceptId){
	return async (dispatch) => {
		try{
			const conceptResponse = await dispatch(loadConcept(libraryId, conceptId));

			const creatives = conceptResponse?.payload?.data?.creatives || [];

			dispatch({
				type: VERSION_GENERATION_ACTIONS.LOAD_CREATIVES,
				payload: {
					creatives,
				},
			});

			return dispatch(loadVariablesForCreatives(libraryId));
		} catch(error){
			return error;
		}
	};
}

export function loadCreativesWithVariables(libraryId, creativeIds){
	return async (dispatch) => {
		try{
			const creativesResponse = await dispatch(fetchCreativesById(libraryId, creativeIds));
			const creatives = creativesResponse?.payload?.data?.items || [];

			dispatch({
				type: VERSION_GENERATION_ACTIONS.LOAD_CREATIVES,
				payload: {
					creatives,
				},
			});

			return dispatch(loadVariablesForCreatives(libraryId));
		} catch(error){
			return error;
		}
	};
}

export function loadVariablesForCreatives(libraryId){
	return async (dispatch, getState) => {
		try {
			await dispatch(loadVersionsByName(libraryId));
			const state = getState();

			const numCreatives = state.versionGeneration?.creatives?.length;
			const sourceVersions = state.versionGeneration?.sourceVersions || [];

			if(!numCreatives){
				return dispatch({
					type: VERSION_GENERATION_ACTIONS.LOAD_CREATIVE_VARIABLES,
					payload: {
						variables: [],
					},
				});
			}

			/*
				We count how many times a variable appears in the versions, and just check
				that the count matches how many creatives there are to determine which variables
				are common to all creatives.

				For simplicity, we just assume that variables with the same name have the same type,
				and don't attempt to do type conflict resolution.
			*/
			const variablesWithCounts = sourceVersions.reduce((prev, version) => {
				const variableEntries = Object.entries(version.variableConfig);

				variableEntries.forEach(([variableName, variable]) => {
					const key = variableName.toLowerCase();
					const existingValue = prev[key];
					if(!existingValue){
						prev[key] = {
							...variable,
							count: 1,
						};
					} else {
						prev[key] = {
							...existingValue,
							count: existingValue.count + 1,
						};
					}
				});

				return prev;
			}, {});

			const variables = Object.values(variablesWithCounts).reduce((prev, value) => {
				const { count, ...variable } = value || {};

				// Filter out variables that aren't common to all creatives, and video variables
				// TODO: We may need to filter out audio and richload variables as well
				if(count === numCreatives && variable.type !== "Video"){
					prev.push(variable);
				}

				return prev;
			}, []);

			return dispatch({
				type: VERSION_GENERATION_ACTIONS.LOAD_CREATIVE_VARIABLES,
				payload: {
					variables,
				},
			});
		} catch(error){
			return error;
		}
	};
}

export function generateAssets(libraryId, data, options = {}){
	/*
		If `options.patch` is true, we keep the existing generated assets, and only replace
		the texts/images that are in `data`, which is useful for regenerating specific assets.

		Otherwise, all existing assets will be discarded before generating new ones.
	*/
	return (dispatch) => {
		if(options.patch){
			dispatch(resetVariationPages());
		}
		return dispatch({
			type: VERSION_GENERATION_ACTIONS.GENERATE_ASSETS_AJAX,
			AJAX: {
				gateway: "ai",
				url: `ui/creative-library/${libraryId}/generate-assets`,
				method: "POST",
				data,
				success: VERSION_GENERATION_ACTIONS.GENERATE_ASSETS_SUCCESS,
				error: VERSION_GENERATION_ACTIONS.GENERATE_ASSETS_ERROR,
			},
			meta: {
				...SUPPRESS_SUCCESS_GROWL,
				buildMessage: buildAssetGenerationFailMessage,
			},
			options,
		});
	};
}

export function createVersionsAndAssets(libraryId, data){
	/*
		`data` is expected to be in the form
		```
			{
				assets: [ // generated assets to be added to the library
					{
						name: "ai_stuff.png",
						path: "https://path.to/jenai/bucket/ai_stuff.png"
					},
					...
				],
				creatives: [
					{
						id: 123,
						versions: [
							{
								name: "SomePrefix - Variation 1",
								variableConfig: {
									background_image: {
										name: "background_image",
										data: "ai_stuff.png", // AI generated image, matches name from above assets list
										prop: "https://path.to/jenai/bucket/ai_stuff.png",
										type: "Image",
										cropMetadata: {
											hasManualRefinements: false,
											canvas: {
												width: 533,
												height: 300,
												top: 0,
												left: 109
											}
										}
									},
									non_refined_image: {
										name: "non_refined_image",
										data: "imageAssetName.png",
										prop: "https://qa-api.flashtalking.net/crm/v1/asset-manager/168488/images/instantAssets/imageAssetName.png",
										type: "Image"
										// No need to include cropMetadata
									},
									cta: {
										name: "cta",
										data: "Glow Brighter",
										prop: "0",
										type: "Text"
									},
									...
								}
							},
							...
						]
					}
				],
			}
		```
	*/

	return (dispatch) => {
		return dispatch({
			type: VERSION_GENERATION_ACTIONS.CREATE_VERSIONS_AND_ASSETS_AJAX,
			AJAX: {
				url: `ui/creative-libraries/${libraryId}/versions-and-assets`,
				method: "POST",
				data,
				success: VERSION_GENERATION_ACTIONS.CREATE_VERSIONS_AND_ASSETS_SUCCESS,
				error: VERSION_GENERATION_ACTIONS.CREATE_VERSIONS_AND_ASSETS_ERROR,
			},
			meta: { buildMessage: buildAddVersionsToLibraryMessage },
		});
	};
}


export function resetVariationPages(){
	return (dispatch) => {
		return dispatch({
			type: VERSION_GENERATION_ACTIONS.RESET_VARIATION_PAGE,
		});
	};
}

export function addPage(){
	return (dispatch) => {
		return dispatch({
			type: VERSION_GENERATION_ACTIONS.ADD_VARIATION_PAGE,
		});
	};
}


export function discardAsset(type, variableName, value){
	return (dispatch) => {
		return dispatch({
			type: VERSION_GENERATION_ACTIONS.DISCARD_ASSET,
			payload: {
				type,
				variableName,
				value,
			},
		});
	};
}

export function getUniqueVersionNames(libraryId, creatives, options = {}){
	/*
		creatives is expected to be an array in the form:
		```
			[
				{ id: 123 },
				{ id: 234 },
				...
			]
		```

		options may include: {
			inAllCreatives, // Filter by version names that are common to all creatives
			status, // Filter by PUBLISHED/UNPUBLISHED/ALL
			searchTerm, // Filter by names containing searchTerm
		}
	*/
	return (dispatch) => {
		return dispatch({
			type: VERSION_GENERATION_ACTIONS.GET_UNIQUE_VERSION_NAMES_AJAX,
			meta: SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				method: "post",
				url: `ui/creative-libraries/${libraryId}/version-names`,
				data: {
					creatives,
					...options,
					rpp: 50,
				},
				success: VERSION_GENERATION_ACTIONS.GET_UNIQUE_VERSION_NAMES_SUCCESS,
				error: VERSION_GENERATION_ACTIONS.GET_UNIQUE_VERSION_NAMES_ERROR,
			},
		});
	};
}

export function getMetadataEntriesByType(libraryId, type) {
	return (dispatch) => {
		return dispatch({
			payload: { type },
			type: VERSION_GENERATION_ACTIONS.GET_METADATA_ENTRIES_BY_TYPE_AJAX,
			meta: SUPPRESS_ALL_GROWL,
			AJAX: {
				gateway: "adm",
				url: `ui/creative-libraries/${libraryId}/${type}`,
				params: { rpp: 50, "filter[status]":"eq:Active" },
				success: VERSION_GENERATION_ACTIONS.GET_METADATA_ENTRIES_BY_TYPE_SUCCESS,
				error: VERSION_GENERATION_ACTIONS.GET_METADATA_ENTRIES_BY_TYPE_ERROR,
			},
		});
	};
}

function initCreativeVariables(state, action){
	return {
		...state,
		isVariablesLoaded: true,
		variables: action.payload.variables,
	};
}

function addSourceVersions(state, action){
	const sourceVersions = action?.payload?.data?.items || [];
	const boundsByVariable = getVariableBounds(sourceVersions);

	return {
		...state,
		sourceVersions,
		sourceVersionName: action?.versionName,
		boundsByVariable,
	};
}

function addValidCreatives(state, action){
	const { creatives = []} = action.payload || {};
	const validCreatives = creatives.filter((creative) => {
		return GENERATE_VERSIONS.shouldDisplay({
			dependentValues: creative,
			isAiVersionGenerationEnabled: true,
		});
	});

	return {
		...state,
		creatives: validCreatives,
	};
}

function initGenerateAssets(state, action){
	const { patch } = action?.options || {};
	return {
		...state,
		isGeneratingAssets: !patch,
		generatedAssets: patch ? state.generatedAssets : {},
	};
}

function addGeneratedAssets(state, action){
	const { patch } = action?.options || {};
	let { numVariationsShown } = state;

	let updatedAssets = action?.payload?.data || {};
	const { texts = [], images = []} = action?.payload?.data || {};

	// Remove duplicates from the generated text assets we can do it before Patching
	// so it will always have effect also on Patched values
	texts.forEach((text) => {
		const { values } = text;
		// Remove duplicates
		text.values = [...new Set(values.map(item => item.value))].map(value => ({ value }));
	});

	if(patch){
		updatedAssets = cloneDeep(state.generatedAssets);

		texts.forEach((text) => {
			const { name, values } = text;
			const existingText = updatedAssets.texts.find(item => item.name === name);
			if(existingText){
				existingText.values = values;
			} else {
				updatedAssets.texts.push(text);
			}
		});

		images.forEach((image) => {
			const { name, values } = image;
			const existingImage = updatedAssets.images.find(item => item.name === name);
			if(existingImage){
				existingImage.values = values;
			} else {
				updatedAssets.images.push(image);
			}
		});
	}

	const variations = generateVariations(updatedAssets);

	return {
		...state,
		isGeneratingAssets: false,
		generatedAssets: updatedAssets,
		variations,
		numVariationsShown: Math.min(numVariationsShown, variations.length),
	};
}

function generateAssetsError(state, action){
	return {
		...state,
		isGeneratingAssets: false,
		generatedAssets: {},
	};
}

function resetVariationsShown(state){
	return {
		...state,
		numVariationsShown: VARIATION_RPP,
	};
}

function increaseVariationsShown(state){
	const numVariations = state.variations.length;

	return {
		...state,
		numVariationsShown: Math.min(state.numVariationsShown + VARIATION_RPP, numVariations),
	};
}

function removeAsset(state, action){
	const { type, variableName, value } = action.payload || {};
	const { variations, numVariationsShown } = state;

	const generatedAssets = cloneDeep(state.generatedAssets);
	const variable = generatedAssets[type].find(item => item.name === variableName);

	if(variable){
		if(type === "images"){
			variable.values = variable.values.filter(item => item.src !== value);
		} else if(type === "texts"){
			variable.values = variable.values.filter(item => item.value !== value);
		}
	}

	function variationFilter(variation){
		return variation[variableName] !== value;
	}

	// We have to slice up the variations to update numVariationsShown properly.
	const shownVariations = variations.slice(0, numVariationsShown);
	const restVariations = variations.slice(numVariationsShown);

	const filteredShownVariations = shownVariations.filter(variationFilter);
	const filteredRestVariations = restVariations.filter(variationFilter);

	const filteredVariations = [
		...filteredShownVariations,
		...filteredRestVariations,
	];

	return {
		...state,
		generatedAssets,
		variations: filteredVariations,
		numVariationsShown: filteredShownVariations.length || VARIATION_RPP,
	};
}


function initAddUniqueVersionNames(state, action){
	return {
		...state,
		uniqueVersionNames: {
			...state.uniqueVersionNames,
			isLoading: true,
		},
	};
}

function addUniqueVersionNames(state, action){
	const { page, totalPages, items = [], totalItems, rpp } = action?.payload?.data || {};
	const prevItems = state.uniqueVersionNames?.items || [];

	return {
		...state,
		uniqueVersionNames: {
			items: [...prevItems, ...items],
			totalItems,
			totalPages,
			rpp,
			page,
		},
	};
}

function addUniqueVersionNamesError(state, action){
	return {
		...state,
		uniqueVersionNames: {
			items: [],
			totalItems: 0,
			totalPages: 1,
			rpp: 50,
			page: 1,
			isLoading: false,
		},
	};
}

function getCreativesById(state, action) {
	return {
		...state,
		isCreativesLoaded: false,
	};
}

function getCreativesByIdSuccess(state, action) {
	return {
		...state,
		isCreativesLoaded: true,
	};
}

function initMetadataEntry(state, action) {
	const { type } = action.payload?.original;

	return {
		...state,
		metadata: {
			...state.metadata,
			[type]: [],
		},
	};
}

function addMetadataEntry(state, action) {
	const { type } = action.payload?.original;
	const generator = (val, key) => {
		if (key === "label") {
			return val?.name;
		} else if (key === "value") {
			return val?.id;
		}

		return val;
	};

	const options = genSelectOpts(action.payload?.data?.items, ["label", "value"], generator) || [];

	return {
		...state,
		metadata: {
			...state.metadata,
			[type]: options,
		},
	};
}


function reinitialize(state){
	return {
		...state,
		...initialState,
	};
}

const handlers = {
	[VERSION_GENERATION_ACTIONS.LOAD_CREATIVE_VARIABLES]: initCreativeVariables,
	[VERSION_GENERATION_ACTIONS.LOAD_CREATIVES]: addValidCreatives,
	[VERSION_GENERATION_ACTIONS.RESET]: reinitialize,

	[VERSION_GENERATION_ACTIONS.GENERATE_ASSETS_AJAX]: initGenerateAssets,
	[VERSION_GENERATION_ACTIONS.GENERATE_ASSETS_SUCCESS]: addGeneratedAssets,
	[VERSION_GENERATION_ACTIONS.GENERATE_ASSETS_ERROR]: generateAssetsError,

	[VERSION_GENERATION_ACTIONS.RESET_VARIATION_PAGE]: resetVariationsShown,
	[VERSION_GENERATION_ACTIONS.ADD_VARIATION_PAGE]: increaseVariationsShown,
	[VERSION_GENERATION_ACTIONS.DISCARD_ASSET]: removeAsset,

	[VERSION_GENERATION_ACTIONS.GET_UNIQUE_VERSION_NAMES_AJAX]: initAddUniqueVersionNames,
	[VERSION_GENERATION_ACTIONS.GET_UNIQUE_VERSION_NAMES_SUCCESS]: addUniqueVersionNames,
	[VERSION_GENERATION_ACTIONS.GET_UNIQUE_VERSION_NAMES_ERROR]: addUniqueVersionNamesError,

	[VERSION_GENERATION_ACTIONS.GET_METADATA_ENTRIES_BY_TYPE_AJAX]: initMetadataEntry,
	[VERSION_GENERATION_ACTIONS.GET_METADATA_ENTRIES_BY_TYPE_SUCCESS]: addMetadataEntry,

	[VERSION_GENERATION_ACTIONS.LOAD_SOURCE_VERSIONS_SUCCESS]: addSourceVersions,

	[CREATIVES_ACTIONS.FETCH_CREATIVES_BY_ID]: getCreativesById,
	[CREATIVES_ACTIONS.FETCH_CREATIVES_BY_ID_SUCCESS]: getCreativesByIdSuccess,
};

export default createReducer(initialState, handlers);

export const REDUCERS = {
	addGeneratedAssets,
	removeAsset,
};
